import React, { useState, useMemo, useEffect } from 'react';

import { countries as nationalitiesJSON } from 'data';
import {
  DateInputWithConfirm,
  InputWithConfirm,
  AutocompleteInputWithConfirm,
  SelectWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  getStringFromDate,
  getDateFromString,
  ContinueButton,
  isValidDate,
} from 'shared-components';

import { VQ } from '../../../../../store/student/types';
import { Relationship, IDate, IOtherTravellers } from '../../../../../types';
import { relationshipLabel } from './utils';
import { useTranslation } from "react-i18next";

interface IProps {
  index: number;

  nationality: string;
  familyName: string;
  givenName: string;
  dob: string;
  sameNationality?: boolean;
  ukVisa?: boolean;
  hasPassport?: boolean;
  passportNumber: string;
  relationship?: (typeof Relationship)[keyof typeof Relationship];
  relationshipOther: string;
  adultOrChild: string;

  onSaveAndClose: (details: IOtherTravellers) => void;
}

const OtherTravellers = (props: IProps) => {
    const { t } = useTranslation('common');
  const { index, onSaveAndClose } = props;
  const [nationality, setNationality] = useState(props.nationality);
  const [familyName, setFamilyName] = useState(props.familyName);
  const [givenName, setGivenName] = useState(props.givenName);
  const [dDob, setDDob] = useState(() => getDateFromString(props.dob));
  const [dDobError, setDDobError] = useState('');
  const [sameNationality, setSameNationality] = useState(props.sameNationality);
  const [ukVisa, setUkVisa] = useState(props.ukVisa);
  const [hasPassport, setHasPassport] = useState(props.hasPassport);
  const [passportNumber, setPassportNumber] = useState(props.passportNumber);
  const [relationship, setrelationship] = useState(props.relationship);
  const [relationshipOther, setrelationshipOther] = useState(props.relationshipOther);
  const [adultOrChild, setAdultOrChild] = useState(props.adultOrChild);

  const nationalities = useMemo(() => [...new Set(nationalitiesJSON.map(n => n.nationality))], []);
  const [nationalitySuggestions, setNationalitySuggestions] = useState(nationalities);

  const saveAndClose = () => {
    onSaveAndClose({
      nationality,
      familyName,
      givenName,
      dob: getStringFromDate(dDob),
      sameNationality,
      ukVisa,
      hasPassport,
      passportNumber,
      relationship,
      relationshipOther,
      adultOrChild,
    });
  };

  const handleOnSaveAndCloseClick = (_event: React.MouseEvent<HTMLButtonElement>) => {
    saveAndClose();
  };

  const validateDate = (dateValue: IDate): string => {
    const { YYYY, DD, MM } = dateValue;

    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        return "Doesn't look like a valid date";
      }
      return '';
    }

    return '';
  };

  const onNationalityChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = nationalities.filter(s => s.toLowerCase().includes(value.toLowerCase()));
    setNationality(value);
    setNationalitySuggestions(suggestions);
  };

  const onBlurNationality = (evt: any) => {
    const value = (evt.currentTarget.value || '').trim();
    const nationality = nationalities.includes(value) ? value : '';
    setNationality(nationality);
    setNationalitySuggestions(nationalities);
  };

  const complete =
    nationality !== '' &&
    sameNationality !== undefined &&
    familyName !== '' &&
    givenName !== '' &&
    dDob.YYYY !== null &&
    dDob.MM !== null &&
    dDob.DD !== null &&
    dDobError === '' &&
    ukVisa !== undefined &&
    hasPassport !== undefined &&
    (hasPassport === true ? passportNumber !== '' : true) &&
    relationship !== undefined &&
    (relationship === Relationship.OTHER ? relationshipOther !== '' : true);

  useEffect(() => {
    if (relationship !== Relationship.OTHER) {
      setrelationshipOther('');
    }
  }, [relationship]);

  useEffect(() => {
    setPassportNumber('');
  }, [hasPassport]);

  return (
    <>
      <SelectWithConfirm
        key={`${VQ.RELATIONSHIP_TO_YOU}-${index}`}
        id={`${VQ.RELATIONSHIP_TO_YOU}-${index}`}
        label={t('common:whatIsThisPersonSRelationshipToY')}
        options={[
          {
            label: relationshipLabel(Relationship.GUARDIAN),
            value: Relationship.GUARDIAN,
          },
          {
            label: relationshipLabel(Relationship.FRIEND),
            value: Relationship.FRIEND,
          },
          {
            label: relationshipLabel(Relationship.SISTER),
            value: Relationship.SISTER,
          },
          {
            label: relationshipLabel(Relationship.BROTHER),
            value: Relationship.BROTHER,
          },
          {
            label: relationshipLabel(Relationship.AUNT),
            value: Relationship.AUNT,
          },
          {
            label: relationshipLabel(Relationship.UNCLE),
            value: Relationship.UNCLE,
          },
          {
            label: relationshipLabel(Relationship.COUSIN),
            value: Relationship.COUSIN,
          },
          {
            label: relationshipLabel(Relationship.GRANDPARENT),
            value: Relationship.GRANDPARENT,
          },
          {
            label: relationshipLabel(Relationship.NON_DEPENDANT),
            value: Relationship.NON_DEPENDANT,
          },
          {
            label: relationshipLabel(Relationship.OTHER),
            value: Relationship.OTHER,
          },
        ]}
        value={relationship}
        onChange={(evt: any) => setrelationship(evt.currentTarget.value)}
      />

      {relationship === Relationship.OTHER ? (
        <InputWithConfirm
          key={`${VQ.RELATIONSHIP_TO_YOU_OTHER}-${index}`}
          id={`${VQ.RELATIONSHIP_TO_YOU_OTHER}-${index}`}
          value={relationshipOther}
          label={t('common:otherRelationship')}
          onChange={(evt: any) => {
            setrelationshipOther(evt.currentTarget.value);
          }}
        />
      ) : null}

      <SelectWithConfirm
        key={`${VQ.ADULT_OR_CHILD}-${index}`}
        id={`${VQ.ADULT_OR_CHILD}-${index}`}
        label={t('common:areTheyAnAdultOrChild')}
        options={[
          { label: 'Adult', value: 'Adult' },
          { label: 'Child', value: 'Child' },
        ]}
        value={adultOrChild}
        onChange={(evt: any) => setAdultOrChild(evt.currentTarget.value)}
      />

      <AutocompleteInputWithConfirm
        key={`${VQ.NATIONALITY}-${index}`}
        id={`${VQ.NATIONALITY}-${index}`}
        value={nationality}
        label={t('common:nationality')}
        onChange={(evt: any) => onNationalityChange(evt)}
        suggestions={nationalitySuggestions}
        onBlur={(evt: any) => onBlurNationality(evt)}
      />

      <RadioOptionsWithConfirm
        key={`${VQ.SAME_NATIONALITY}-${index}`}
        id={`${VQ.SAME_NATIONALITY}-${index}`}
        label={t('common:haveTheyAlwaysHadTheSameNational')}
        onClick={(question: IQuestion) => setSameNationality(question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: sameNationality === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: sameNationality === false,
          },
        ]}
      />

      <InputWithConfirm
        key={`${VQ.FAMILY_NAME}-${index}`}
        id={`${VQ.FAMILY_NAME}-${index}`}
        value={familyName}
        label={t('common:familyName2')}
        onChange={(evt: any) => {
          setFamilyName(evt.currentTarget.value);
        }}
      />

      <InputWithConfirm
        key={`${VQ.GIVEN_NAME}-${index}`}
        id={`${VQ.GIVEN_NAME}-${index}`}
        value={givenName}
        label={t('common:givenName')}
        onChange={(evt: any) => {
          setGivenName(evt.currentTarget.value);
        }}
      />

      <DateInputWithConfirm
        key={`${VQ.DOB}-${index}`}
        id={`${VQ.DOB}-${index}`}
        label={t('common:dateOfBirth2')}
        value={dDob}
        errorText={dDobError}
        onChangeDD={(evt: any) => setDDob({ ...dDob, DD: evt.target.value })}
        onChangeMM={(evt: any) => setDDob({ ...dDob, MM: evt.target.value })}
        onChangeYY={(evt: any) => setDDob({ ...dDob, YYYY: evt.target.value })}
        onBlur={(_evt: any) => setDDobError(validateDate(dDob))}
      />

      <RadioOptionsWithConfirm
        key={`${VQ.HAS_UK_VISA}-${index}`}
        id={`${VQ.HAS_UK_VISA}-${index}`}
        label={t('common:doTheyHaveAVisaForTheUk')}
        onClick={(question: IQuestion) => setUkVisa(question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: ukVisa === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: ukVisa === false,
          },
        ]}
      />

      <RadioOptionsWithConfirm
        key={`${VQ.HAS_PASSPORT}-${index}`}
        id={`${VQ.HAS_PASSPORT}-${index}`}
        label={t('common:doTheyHaveAPassportOrTravelDocum')}
        onClick={(question: IQuestion) => setHasPassport(question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasPassport === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: hasPassport === false,
          },
        ]}
      />

      {hasPassport ? (
        <InputWithConfirm
          key={`${VQ.PASSPORT_NUMBER}-${index}`}
          id={`${VQ.PASSPORT_NUMBER}-${index}`}
          value={passportNumber}
          label={t('common:currentPassportOrTravelDocumentN')}
          onChange={(evt: any) => setPassportNumber(evt.currentTarget.value)}
        />
      ) : null}

      <ContinueButton index={index} complete={complete} handleOnSaveAndCloseClick={handleOnSaveAndCloseClick} />
    </>
  );
};

export default OtherTravellers;
